import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { RouteComponentProps } from "react-router";
import { Box } from "@material-ui/core";
import {
  CallState,
  Cap1DisruptionAddOnChoiceProperties,
  CAP1_VIEW_BOOKING_ADD_ON,
  CFAR_ADD_ON_CHOICE,
  DISRUPTION_ADD_ON_CHOICE,
} from "redmond";
import { Location } from "history";
import { B2BLoadingPopup, B2BSpinner, HtsLoadError } from "halifax";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { CfarDetails, Cap1AirCfarHtsMerchandising } from "../cfar";
import { DisruptionProtectionDetails } from "../disruptionProtection";
import { MobileAddOnCustomizeConnectorProps } from "./container";
import { AddOnCustomizeStep, AddOnCustomizeStepAlias } from "./types";
import { PATH_BOOK } from "../../../../../utils/urlPaths";
import {
  CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY,
  CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY_CFAR_FDA,
  getExperimentVariant,
  HTS_FDA_MARKETING,
  useExperiments,
  AVAILABLE,
  XMOD_V1_EXPERIMENT,
} from "../../../../../context/experiments";
import { useBusinessExperimentsById } from "@capone/experiments";
import { AddOnTestimonyCarousel } from "../AddOnTestimonyCarousel";
import { ClientContext } from "../../../../../App";
import * as constants from "./constants";

import "./styles.scss";
import { Cap1AirDisruptionHtsMerchandising } from "../disruptionProtection/Cap1AirDisruptionHtsMerchandising";
import { useHtsAirFlexibility } from "../hooks/useHtsAirFlexibility";
import { useHtsMerchandisingScripts } from "../hooks/useHtsMerchandisingScripts";

export interface IMobileAddOnCustomizeProps
  extends MobileAddOnCustomizeConnectorProps,
    RouteComponentProps {
  // note: this callback function is used to overwrite the default (final) on-continue behaviour
  goToCheckout?: () => void;
  goBack: () => void;
  currentStep: AddOnCustomizeStepAlias;
  setCurrentStep: React.Dispatch<React.SetStateAction<AddOnCustomizeStepAlias>>;
  isCfarEnabled: boolean;
  isDisruptionProtectionEnabled: boolean;
}

export const MobileAddOnCustomize = (props: IMobileAddOnCustomizeProps) => {
  const {
    history,
    populateFlightBookQueryParams,
    fetchAncillaryOffer,
    goToCheckout = () => {
      populateFlightBookQueryParams({ history });
    },
    goBack,
    currentStep,
    setCurrentStep,
    setSelectedCfarId,
    setSelectedDisruptionProtectionId,
    fetchAncillaryOfferCallState,
    cfarAddOnChoiceProperties,
    dpAddOnChoiceProperties,
    isCfarAvailable,
    isCfarEnabled,
    isDisruptionProtectionAvailable,
    isDisruptionProtectionEnabled,
    viewBookingAddOnProperties,
    dpOfferPropertiesForViewBookingAddOn,
    cfarOfferPropertiesForViewBookingAddOn,
    hasActiveRefundableFare,
    cfarSocialVariant,
    cfarDiscountProperties,
    cfarOffer,
    hasTravelFusionFareBrand,
    disruptionOffer,
    selectedDisruptionProtectionId,
    fdaProductVariant,
  } = props;
  const isAncillaryExposed =
    (isCfarAvailable && isCfarEnabled) ||
    (isDisruptionProtectionAvailable && isDisruptionProtectionEnabled);
  const prevStep = useRef<AddOnCustomizeStepAlias>(currentStep);
  const cfarDiscount =
    cfarOffer && cfarOffer.length > 0 ? cfarOffer[0].discount : null;

  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;

  const expState = useExperiments();

  const isDisruptionMerchindisingEnabled = useMemo(
    () =>
      getExperimentVariant(expState.experiments, HTS_FDA_MARKETING) ===
      AVAILABLE,
    [expState]
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const freeFdaVariant = useBusinessExperimentsById("corp-free-fda")?.variant;
  const isFreeFdaEnabled =
    freeFdaVariant === "available" || freeFdaVariant === "available_phase2";

  const { isHtsCfar, htsCfarOffer, isHtsDisruption, htsDisruptionOffer } =
    useHtsAirFlexibility({
      disruptionOffer,
      cfarOffer,
      hasActiveRefundableFare,
    });

  const handleCfarAddOnChoiceEvent = () => {
    trackEvent({
      eventName: CFAR_ADD_ON_CHOICE,
      properties: {
        ...cfarAddOnChoiceProperties,
        ...{
          cfar_discount_original_premium:
            cfarDiscountProperties.original_premium,
          cfar_discount_percentage: cfarDiscountProperties.discount_percentage,
        },
        success: true,
      },
    });
  };

  const handleDpAddOnChoiceEvent = () => {
    trackEvent({
      eventName: DISRUPTION_ADD_ON_CHOICE,
      properties: {
        ...dpAddOnChoiceProperties,
      } as Cap1DisruptionAddOnChoiceProperties,
    });
  };

  const onContinue = () => {
    setCurrentStep((step) => step + 1);
  };

  const resetCfarSelection = useCallback(() => {
    setSelectedCfarId(null);
  }, [setSelectedCfarId]);

  const resetDisruptionSelection = useCallback(() => {
    setSelectedDisruptionProtectionId(null);
  }, [setSelectedDisruptionProtectionId]);

  const {
    htsCfarComplete,
    htsDisruptionComplete,
    htsCfarLoadingError,
    htsDisruptionLoadingError,
    htsCfarRetryCount,
    htsDisruptionRetryCount,
    onHtsCfarLoadSuccess,
    onHtsDisruptionLoadSuccess,
    onHtsCfarLoadError,
    onHtsDisruptionLoadError,
    onHtsCfarRetry,
    onHtsDisruptionRetry,
  } = useHtsMerchandisingScripts({
    isHtsCfar,
    isHtsDisruption,
    isCfarAvailable: !hasActiveRefundableFare && isCfarAvailable,
    isDisruptionProtectionAvailable,
    resetCfarSelection,
    resetDisruptionSelection,
  });

  const addOnCustomizeSteps = {
    [AddOnCustomizeStepAlias.GoBack]: AddOnCustomizeStep.GoBack,
    [AddOnCustomizeStepAlias.AddOn1]: AddOnCustomizeStep.DisruptionProtection,
    [AddOnCustomizeStepAlias.AddOn2]: AddOnCustomizeStep.CFAR,
    [AddOnCustomizeStepAlias.GoToCheckout]: AddOnCustomizeStep.GoToCheckout,
  };

  const isDisruptionStep =
    addOnCustomizeSteps[currentStep] ===
    AddOnCustomizeStep.DisruptionProtection;
  const isCfarStep =
    addOnCustomizeSteps[currentStep] === AddOnCustomizeStep.CFAR;

  const isHtsDisruptionLoaded =
    !isDisruptionStep ||
    !isHtsDisruption ||
    htsDisruptionComplete ||
    htsDisruptionLoadingError;
  const isHtsCfarLoaded =
    !isCfarStep ||
    hasActiveRefundableFare ||
    !isHtsCfar ||
    htsCfarComplete ||
    htsCfarLoadingError;

  useEffect(() => {
    if (fetchAncillaryOfferCallState === CallState.InProcess) return;
    const processStep = (skipCondition: boolean) => {
      // note: when a step isn't enabled (e.g. cfar experiment is turned off or the cfar offer is not available),
      // that step needs to be skipped
      if (skipCondition) {
        const stepAfter = currentStep + 1;
        const stepBefore = currentStep - 1;

        // note: when none of the ancillary offers is exposed to the user, consider either GoBack or GoToCheckout
        if (!isAncillaryExposed) {
          const { prevPath } =
            (history.location as Location<{ prevPath: string }>)?.state ?? {};

          // note: the user is trying to go-back from flights checkout - it should redirect to PF overview
          if (prevPath === PATH_BOOK) {
            setCurrentStep(AddOnCustomizeStepAlias.GoBack);
          } else {
            setCurrentStep(AddOnCustomizeStepAlias.GoToCheckout);
          }
        }
        // note: this check of prevStep provides a robust
        // way of determining the step before the user took their action.
        // it should be noted that it is possible to have multiple unavailable
        // ancillary products in this flow, so when handling how to navigate
        // between them, we just need to know if the previous step was
        // _any_ step later on, not just the next step.
        // (the comment in the PR of this commit provides a more thorough explanation)
        else if (prevStep.current > currentStep) {
          setCurrentStep(stepBefore);
        } else {
          setCurrentStep(stepAfter);
        }
      } else {
        prevStep.current = currentStep;
      }
    };

    switch (addOnCustomizeSteps[currentStep]) {
      case AddOnCustomizeStep.GoBack:
        prevStep.current = currentStep;
        goBack();
        break;
      case AddOnCustomizeStep.GoToCheckout:
        prevStep.current = currentStep;
        goToCheckout();
        break;
      case AddOnCustomizeStep.DisruptionProtection:
        processStep(!isDisruptionProtectionAvailable);
        break;
      case AddOnCustomizeStep.CFAR:
        processStep(!isCfarAvailable);
        break;
      default:
        break;
    }
  }, [
    currentStep,
    isCfarAvailable,
    isDisruptionProtectionAvailable,
    goBack,
    goToCheckout,
    setCurrentStep,
    fetchAncillaryOfferCallState,
  ]);

  useEffect(() => {
    /*
      note: after the user refreshes the page on flight checkout, and clicks on "customize" in the progress bar,
      it needs to re-fetch ancillary offers
    */
    if (fetchAncillaryOfferCallState === CallState.NotCalled) {
      fetchAncillaryOffer({
        preserveCfarId: hasActiveRefundableFare,
      });
    }
  }, [fetchAncillaryOfferCallState, hasActiveRefundableFare]);

  useEffect(
    () => () => {
      setCurrentStep(AddOnCustomizeStepAlias.AddOn1);
    },
    []
  );

  useEffect(() => {
    if (
      fetchAncillaryOfferCallState === CallState.Success &&
      (isCfarAvailable || isDisruptionProtectionAvailable)
    ) {
      trackEvent({
        eventName: CAP1_VIEW_BOOKING_ADD_ON,
        properties: {
          ...viewBookingAddOnProperties,
          ...(isDisruptionProtectionAvailable &&
            dpOfferPropertiesForViewBookingAddOn),
          ...(isCfarAvailable && cfarOfferPropertiesForViewBookingAddOn),
          ...{
            cfar_discount_original_premium:
              cfarDiscount?.originalPremiumAmount.fiat.value,
            cfar_discount_percentage: cfarDiscount?.discountPercentage,
          },
        },
      });
    }
  }, [
    fetchAncillaryOfferCallState,
    isCfarAvailable,
    isDisruptionProtectionAvailable,
  ]);

  const hasDisruptionProtectionAddOnToShow =
    addOnCustomizeSteps[currentStep] ===
      AddOnCustomizeStep.DisruptionProtection &&
    isDisruptionProtectionAvailable;
  const hasCfarAddOnToShow =
    addOnCustomizeSteps[currentStep] === AddOnCustomizeStep.CFAR &&
    isCfarAvailable;

  const cap1AirCfarHtsMerchandising = useMemo(
    () =>
      isHtsCfar ? (
        <Cap1AirCfarHtsMerchandising
          cfarOffer={htsCfarOffer}
          onContinue={() => {
            handleCfarAddOnChoiceEvent();
            onContinue();
          }}
          onLoadError={onHtsCfarLoadError}
          onLoadSuccess={onHtsCfarLoadSuccess}
          isLoading={!isHtsCfarLoaded}
          isError={htsCfarLoadingError}
          retryCount={htsCfarRetryCount}
        />
      ) : null,
    [
      isHtsCfar,
      htsCfarOffer,
      populateFlightBookQueryParams,
      history,
      handleCfarAddOnChoiceEvent,
      onContinue,
      onHtsCfarLoadSuccess,
      isHtsCfarLoaded,
    ]
  );

  const cap1AirDisruptionHtsMerchandising = useMemo(
    () =>
      isHtsDisruption ? (
        <Cap1AirDisruptionHtsMerchandising
          disruptionOffer={htsDisruptionOffer}
          onContinue={() => {
            handleDpAddOnChoiceEvent();
            onContinue();
          }}
          selectedDisruptionProtectionId={selectedDisruptionProtectionId}
          onLoadSuccess={onHtsDisruptionLoadSuccess}
          onLoadError={onHtsDisruptionLoadError}
          isLoading={!isHtsDisruptionLoaded}
          isError={htsDisruptionLoadingError}
          retryCount={htsDisruptionRetryCount}
        />
      ) : null,
    [
      isHtsDisruption,
      htsDisruptionOffer,
      selectedDisruptionProtectionId,
      handleDpAddOnChoiceEvent,
      onContinue,
      onHtsDisruptionLoadSuccess,
      isHtsDisruptionLoaded,
    ]
  );

  const displayDisruptionMerch =
    isDisruptionMerchindisingEnabled && cap1AirDisruptionHtsMerchandising;

  const disruptionContent =
    isDisruptionProtectionAvailable &&
    (displayDisruptionMerch ? (
      cap1AirDisruptionHtsMerchandising
    ) : (
      <DisruptionProtectionDetails
        onContinue={() => {
          handleDpAddOnChoiceEvent();
          onContinue();
        }}
        onNoDisruptionProtectionOffer={onContinue}
        cardContentProps={{
          disablePartialScroll: true,
        }}
      />
    ));

  return (
    <Box className="mobile-add-on-customize-root">
      {fetchAncillaryOfferCallState === CallState.InProcess && (
        <B2BLoadingPopup
          open
          message="for offers"
          image={clientContext.searchImage ?? ""}
          popupSize="mobile"
          isXmod={isXmodRedesign}
        />
      )}
      <Box className="mobile-add-on-customize-container">
        {!(isHtsCfarLoaded && isHtsDisruptionLoaded) && (
          <Box className="hts-mobile-loading-spinner-container">
            <B2BSpinner
              className="hts-mobile-loading-spinner"
              style={{ width: "48px", height: "48px" }}
            />
          </Box>
        )}
        {isDisruptionStep && htsDisruptionLoadingError && (
          <HtsLoadError
            header={constants.getHtsErrorHeaderDisruption(isFreeFdaEnabled, fdaProductVariant)}
            message={constants.HtsErrorMessage}
            handleRetry={onHtsDisruptionRetry}
          />
        )}
        {isCfarStep && htsCfarLoadingError && (
          <HtsLoadError
            header={constants.HtsErrorHeaderCfar}
            message={constants.HtsErrorMessage}
            handleRetry={onHtsCfarRetry}
          />
        )}
        {addOnCustomizeSteps[currentStep] ===
          AddOnCustomizeStep.DisruptionProtection && disruptionContent}
        {addOnCustomizeSteps[currentStep] === AddOnCustomizeStep.CFAR &&
          isCfarAvailable &&
          !cap1AirCfarHtsMerchandising && (
            <CfarDetails
              isAgentPortal={isAgentPortal}
              onContinue={() => {
                handleCfarAddOnChoiceEvent();
                onContinue();
              }}
              onNoCfarOffer={onContinue}
              cardContentProps={{
                disablePartialScroll: true,
              }}
              topBannerEnabled
              hasTravelFusionFareBrand={hasTravelFusionFareBrand}
              isXmodRedesign={isXmodRedesign}
            />
          )}

        {addOnCustomizeSteps[currentStep] === AddOnCustomizeStep.CFAR &&
          isCfarAvailable &&
          cap1AirCfarHtsMerchandising}
      </Box>
      {cfarSocialVariant === CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY_CFAR_FDA && (
        <AddOnTestimonyCarousel
          hasCfarAddOnToShow={hasCfarAddOnToShow}
          hasDisruptionProtectionAddOnToShow={
            hasDisruptionProtectionAddOnToShow
          }
          showArrows={false}
        />
      )}
      {cfarSocialVariant === CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY && (
        <AddOnTestimonyCarousel
          // For the DP only variant, this carousel element behaves as if CFAR is not offered (thus no CFAR testimonies).
          hasCfarAddOnToShow={false}
          hasDisruptionProtectionAddOnToShow={
            hasDisruptionProtectionAddOnToShow
          }
          showArrows={false}
        />
      )}
    </Box>
  );
};
